/**
 * Hook personalizado para gerenciar categorias.
 *
 * Este hook fornece funcionalidades para buscar, criar, editar e excluir categorias,
 * além de gerenciar o estado de carregamento e armazenar os dados das categorias.
 *
 * @module useCategories
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

/**
 * Hook que gerencia operações CRUD para categorias.
 *
 * @returns {Object} Objeto contendo estados e funções para manipular categorias
 * @returns {Array} returns.categories - Lista de categorias
 * @returns {Array} returns.numResources - Número de recursos por categoria
 * @returns {boolean} returns.isLoading - Estado de carregamento
 * @returns {Function} returns.fetchCategories - Função para buscar categorias
 * @returns {Function} returns.createCategory - Função para criar uma categoria
 * @returns {Function} returns.deleteCategory - Função para excluir uma categoria
 * @returns {Function} returns.editCategory - Função para editar uma categoria
 */
export const useCategories = () => {
  const { t } = useTranslation();

  // Estados para armazenar dados e controlar carregamento
  const [categories, setCategories] = useState([]);
  const [numResources, setNumResources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Estados do Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  /**
   * Busca todas as categorias da API.
   *
   * @async
   * @function fetchCategories
   */
  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('categories');
      setCategories(data.resources);
      setNumResources(data.numResources);
    } catch (err) {
      console.error('Error when searching categories', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cria uma nova categoria.
   *
   * @async
   * @function createCategory
   * @param {string} name - Nome da categoria a ser criada
   */
  const createCategory = async (name) => {
    await api.post('create-categories', { name });
    fetchCategories();
  };

  /**
   * Exclui uma categoria pelo ID.
   *
   * @async
   * @function deleteCategory
   * @param {string|number} categoryId - ID da categoria a ser excluída
   */
  const deleteCategory = async (categoryId) => {
    try {
      await api.delete(`categories/${categoryId}`);
      fetchCategories();
      setSnackbarSeverity('success');
      setSnackbarMessage(t('category.deleted_successfully'));
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
      setSnackbarSeverity('error');
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data?.error?.includes('foreign key constraint')
      ) {
        setSnackbarMessage(t('category.has_linked_resources'));
      } else {
        setSnackbarMessage('category.delete_error');
      }
    } finally {
      setSnackbarOpen(true);
    }
  };

  /**
   * Atualiza o nome de uma categoria existente.
   *
   * @async
   * @function editCategory
   * @param {string|number} categoryId - ID da categoria a ser editada
   * @param {string} updatedName - Novo nome para a categoria
   */
  const editCategory = async (categoryId, updatedName) => {
    await api.put(`categories/${categoryId}`, { name: updatedName });
    fetchCategories();
  };

  // Carrega as categorias quando o componente é montado
  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    numResources,
    isLoading,
    fetchCategories,
    createCategory,
    deleteCategory,
    editCategory,
    // Snackbar control
    snackbarMessage,
    snackbarSeverity,
    snackbarOpen,
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
  };
};
